import { useEffect, useRef, useState } from 'react'
import { Link, useBlocker, useSearchParams } from 'react-router-dom'
import type {
  TShowSeries,
  TSeriesMatchingRequest,
  TSeriesMatchingPreview,
  TSeriesMatchingResponse,
} from '@schemas'
import {
  useSeriesMatching,
  usePreviewSeriesMatching,
  useApplySeriesMatching,
  useSeriesEditions,
} from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { parseApiErrorMessage, parseApiErrorPayload } from '@/lib/api-errors'
import { isSeriesAccessError } from './series-draft'
import { suggestedMatches, suggestedMatchRequest } from './series-matching-suggestions'

type Class = TSeriesMatchingResponse['classes'][number]
type Restriction = NonNullable<TSeriesMatchingResponse['editions'][number]['ownershipRestriction']>
const NOT_RUN_HERE: Record<Restriction, string> = {
  read_only:
    'is a read-only copy on this installation. Its matches can change once a handover makes this installation run it.',
  outgoing:
    'is being handed over to another installation. Ask an administrator to complete or recover that handover before changing its matches.',
  inactive: 'is now run by another installation, so its matches cannot be changed here.',
  not_owned: 'is not run by this installation, so its matches cannot be changed here.',
}
const selectStyle = 'w-full rounded-md border bg-background p-2 text-sm'
export function SeriesMatchingWorkspace({
  series,
  onDenied,
}: {
  series: TShowSeries
  onDenied: () => void
}) {
  const [params, setParams] = useSearchParams()
  const years = params.get('years') || undefined
  const query = useSeriesMatching(series.id, years),
    editions = useSeriesEditions(series.id)
  const previewMutation = usePreviewSeriesMatching(series.id),
    save = useApplySeriesMatching(series.id)
  const [comparisonRevision, setComparisonRevision] = useState('')
  const [stale, setStale] = useState(false)
  const [selected, setSelected] = useState<string[]>([]),
    [search, setSearch] = useState(''),
    [division, setDivision] = useState('all'),
    [status, setStatus] = useState('unresolved'),
    [limit, setLimit] = useState(50)
  const [candidateLimit, setCandidateLimit] = useState(6)
  const [skippedSuggestions, setSkippedSuggestions] = useState<string[]>([])
  const [suggestionTypes, setSuggestionTypes] = useState<
    Record<string, { model: string; subtype: string }>
  >({})
  const [editingClassIds, setEditingClassIds] = useState<string[]>([])
  const [mode, setMode] = useState<'list' | 'compare' | 'review' | 'result'>('list')
  const [model, setModel] = useState(''),
    [subtype, setSubtype] = useState(''),
    [identity, setIdentity] = useState('new'),
    [label, setLabel] = useState(''),
    [reason, setReason] = useState('')
  const [divisionAck, setDivisionAck] = useState(false)
  const [draft, setDraft] = useState<TSeriesMatchingRequest | null>(null),
    [preview, setPreview] = useState<TSeriesMatchingPreview | null>(null),
    [error, setError] = useState(''),
    [message, setMessage] = useState('')
  const [receipt, setReceipt] = useState<{
    id: string
    identities: string[]
    summary: string[]
  } | null>(null)
  const heading = useRef<HTMLHeadingElement>(null),
    lastReadable = useRef<string[] | null>(null)
  const busy = save.isPending || previewMutation.isPending
  const dirty =
    Boolean(draft) ||
    mode === 'compare' ||
    skippedSuggestions.length > 0 ||
    Object.keys(suggestionTypes).length > 0
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      (dirty || busy) &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search)
  )
  const data = query.data
  const rows = (data?.classes ?? []).filter(c => selected.includes(c.id))
  const allEditions = editions.data?.pages.flatMap(p => p.editions) ?? []
  const chosenYears = data?.editions.map(e => e.year) ?? []
  const targetModel = data?.models.find(m => m.id === model)
  const activeRows = [
    ...(data?.identities.filter(i => !i.retired) ?? []),
    ...(draft?.newIdentities ?? []),
  ]
  const editable = (c: Class) => Boolean(data?.editions.find(e => e.showId === c.showId)?.canEdit)
  const canReview = rows.length > 0 && rows.every(editable)
  const selectedIdentity =
    data?.identities.find(i => i.id === identity) ??
    draft?.newIdentities.find(i => i.id === identity)
  const chosenModel = identity === 'new' ? model : selectedIdentity?.catalogClassId
  const chosenSubtype = identity === 'new' ? subtype || null : selectedIdentity?.catalogSubtypeId
  const needsRepair = rows.some(
    c =>
      c.catalogClassId !== chosenModel || (c.catalogSubtypeId ?? null) !== (chosenSubtype ?? null)
  )
  const deniedRepair = Boolean(needsRepair && !data?.canRepair)
  const groupValid = rows.length > 0 && new Set(rows.map(c => c.showId)).size === rows.length
  const cannotSaveMatch =
    busy ||
    !canReview ||
    !groupValid ||
    !identity ||
    !chosenModel ||
    deniedRepair ||
    (identity === 'new' &&
      (!model || !label.trim() || Boolean(targetModel?.subtypes.length && !subtype)))
  const hasOtherDraft = draft?.operations.some(
    o => !editingClassIds.includes(o.classId) && !selected.includes(o.classId)
  )
  const suggestions = data
    ? suggestedMatches(data)
        .filter(g => !g.classes.some(c => draft?.operations.some(o => o.classId === c.id)))
        .map(g => ({ ...g, ...suggestionTypes[g.key] }))
    : []
  const suggestionProblem = (g: (typeof suggestions)[number]) => {
    if (g.problem) return g.problem
    const needsTypeRepair = g.classes.some(
      c => c.catalogClassId !== g.model || (c.catalogSubtypeId ?? '') !== g.subtype
    )
    if (needsTypeRepair && !data?.canRepair)
      return 'An administrator must set the missing exhibit type.'
    const type = data?.models.find(m => m.id === g.model)
    // Editors can reuse an existing match without access to the administrator catalog.
    if (!g.model || ((!g.identityId || needsTypeRepair) && !type)) return 'Choose an exhibit type.'
    if (type?.subtypes.length && !g.subtype) return 'Choose a subtype.'
    return undefined
  }
  const includedSuggestions = suggestions.filter(
    g => !skippedSuggestions.includes(g.key) && !suggestionProblem(g)
  )
  function confirmSuggestions() {
    if (!data || !includedSuggestions.length || includedSuggestions.some(suggestionProblem)) return
    const input = suggestedMatchRequest(data, includedSuggestions)
    void review(
      draft
        ? {
            ...input,
            revision: draft.revision,
            operations: [...draft.operations, ...input.operations],
            repairs: [...draft.repairs, ...input.repairs],
            newIdentities: [...draft.newIdentities, ...input.newIdentities],
            notes: { ...draft.notes, ...input.notes },
          }
        : input,
      true
    )
  }
  const reportPath = `/show-series/${series.id}/reports`
  const suppliedReturn = params.get('returnTo')
  const returnTo =
    suppliedReturn && (suppliedReturn === reportPath || suppliedReturn.startsWith(`${reportPath}?`))
      ? suppliedReturn
      : reportPath
  const reportUrl =
    returnTo !== reportPath
      ? returnTo
      : receipt?.identities.length
        ? (() => {
            const u = new URL(returnTo, window.location.origin)
            u.searchParams.set('identityIds', receipt.identities.join(','))
            u.searchParams.set('tab', 'classes')
            return u.pathname + u.search
          })()
        : returnTo
  useEffect(() => {
    if (editions.hasNextPage && !editions.isFetching) void editions.fetchNextPage()
  }, [editions.hasNextPage, editions.isFetching, editions.fetchNextPage])
  useEffect(() => {
    heading.current?.focus()
  }, [mode])
  useEffect(() => {
    if (!dirty && !busy) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, busy])
  useEffect(() => {
    if (isSeriesAccessError(query.error) || isSeriesAccessError(editions.error)) {
      onDenied()
      return
    }
    if (!data || query.isFetching) return
    const ids = data.editions.map(e => e.showId)
    if (lastReadable.current?.some(id => !ids.includes(id))) {
      setDraft(null)
      setPreview(null)
      setSelected([])
      onDenied()
      return
    }
    lastReadable.current = ids
  }, [data, query.error, query.isFetching, editions.error, onDenied])
  function changeMode(next: typeof mode) {
    setMode(next)
    setError('')
  }
  function compare(c: Class) {
    setComparisonRevision(data?.revision ?? '')
    setStale(false)
    setCandidateLimit(6)
    const pending = draft?.operations.find(o => o.classId === c.id)
    const group = pending?.identityId
      ? draft!.operations.filter(o => o.identityId === pending.identityId).map(o => o.classId)
      : [c.id]
    setEditingClassIds(group)
    setSelected(group)
    setModel(c.catalogClassId ?? '')
    setSubtype(c.catalogSubtypeId ?? '')
    setIdentity(pending?.identityId ?? c.identityId ?? (data?.canCreateIdentity ? 'new' : ''))
    setLabel(c.effectiveName)
    setReason(draft?.notes[c.id] ?? '')
    setDivisionAck(
      group.every(id => draft?.operations.find(o => o.classId === id)?.acknowledgeDivisionChange)
    )
    setSearch('')
    changeMode('compare')
  }
  function discard() {
    setSkippedSuggestions([])
    setSuggestionTypes({})
    setDraft(null)
    setPreview(null)
    setSelected([])
    setReason('')
    changeMode('list')
  }
  async function review(input: TSeriesMatchingRequest, saveImmediately = false) {
    setStale(false)
    setDraft(input)
    setPreview(null)
    setError('')
    if (!saveImmediately) setMode('review')
    try {
      const reviewed = await previewMutation.mutateAsync(input)
      setPreview(reviewed)
      // Shows the edition's restriction when the handover happened after these classes loaded.
      if (reviewed.blockers.some(b => b.code === 'SHOW_NOT_ACTIVE')) void query.refetch()
      if (saveImmediately && reviewed.canApply) await apply(input, reviewed)
      else setMode('review')
    } catch (e) {
      setMode('review')
      if (isSeriesAccessError(e)) onDenied()
      else {
        setError(parseApiErrorMessage(e, 'Could not review these changes.'))
        if (e instanceof Error && 'status' in e && e.status === 409) {
          setStale(true)
          setDivisionAck(false)
          setDraft(current =>
            current
              ? {
                  ...current,
                  operations: current.operations.map(o => ({
                    ...o,
                    acknowledgeDivisionChange: false,
                  })),
                }
              : null
          )
          void query.refetch()
        }
      }
    }
  }
  function stage(state: 'assigned' | 'unmatched' | 'unresolved', saveImmediately = true) {
    if (!data) return
    const id =
      state === 'assigned' ? (identity === 'new' ? crypto.randomUUID() : identity) : undefined
    const operations = rows.map(c => ({
      classId: c.id,
      showId: c.showId,
      state,
      ...(id ? { identityId: id } : {}),
      acknowledgeDivisionChange: divisionAck,
    }))
    const replaced = new Set([...editingClassIds, ...selected])
    setEditingClassIds(selected)
    const note =
      reason.trim() ||
      (state === 'unmatched'
        ? 'Exclude selected classes from year-to-year comparisons'
        : state === 'unresolved'
          ? 'Review previously excluded classes again'
          : 'Match selected classes across years')
    const allOperations = [
      ...(draft?.operations ?? []).filter(o => !replaced.has(o.classId)),
      ...operations,
    ]
    const used = new Set(allOperations.flatMap(o => (o.identityId ? [o.identityId] : [])))
    const newIdentities = [
      ...(draft?.newIdentities ?? []).filter(i => used.has(i.id)),
      ...(state === 'assigned' && identity === 'new'
        ? [
            {
              id: id!,
              label,
              catalogClassId: model,
              catalogSubtypeId: subtype || null,
              divisionCode: rows[0].division.code,
            },
          ]
        : []),
    ]
    const repairs = [
      ...(draft?.repairs ?? []).filter(r => !replaced.has(r.classId)),
      ...(state === 'assigned' &&
      (needsRepair ||
        targetModel?.retired ||
        targetModel?.subtypes.find(t => t.id === subtype)?.retired)
        ? rows
            .filter(
              c =>
                c.catalogClassId !== chosenModel ||
                (c.catalogSubtypeId ?? null) !== (chosenSubtype ?? null) ||
                targetModel?.retired ||
                targetModel?.subtypes.find(t => t.id === subtype)?.retired
            )
            .map(c => ({
              classId: c.id,
              catalogClassId: chosenModel!,
              catalogSubtypeId: chosenSubtype ?? null,
              reason:
                reason.trim() ||
                `Set exhibit type to ${valueName(chosenModel)}${chosenSubtype ? ` / ${valueName(chosenSubtype)}` : ''} to match the selected classes across years`,
            }))
        : []),
    ]
    void review(
      {
        showIds: data.editions.map(e => e.showId),
        operations: allOperations,
        notes: Object.fromEntries(
          allOperations.map(o => [
            o.classId,
            selected.includes(o.classId)
              ? note
              : (draft?.notes[o.classId] ?? draft?.reason ?? note),
          ])
        ),
        newIdentities,
        repairs,
        revision: draft?.revision ?? (comparisonRevision || data.revision),
        reason: 'Save class matching changes',
      },
      saveImmediately
    )
  }
  async function apply(input = draft, reviewed = preview) {
    if (!input || !reviewed?.canApply) return
    setError('')
    try {
      const result = await save.mutateAsync({
        ...input,
        reviewToken: reviewed.reviewToken,
        reviewedAt: reviewed.reviewedAt,
      })
      setReceipt({
        id: result.receiptId,
        identities: result.identityIds,
        summary: input.operations.map(
          o =>
            `${className(o.classId)} — ${o.state === 'assigned' ? `Matched to ${input.newIdentities.find(i => i.id === o.identityId)?.label ?? valueName(o.identityId)}` : o.state === 'unmatched' ? 'Excluded from comparison' : 'Ready to review again'}`
        ),
      })
      setDraft(null)
      setPreview(null)
      setSelected([])
      setSkippedSuggestions([])
      setSuggestionTypes({})
      setMessage('Your changes were saved.')
      setMode('result')
    } catch (e) {
      setMode('review')
      if (isSeriesAccessError(e)) onDenied()
      else if (parseApiErrorPayload(e)?.code === 'SHOW_NOT_ACTIVE') {
        // A show stopped being run here after the review. The class details are not stale.
        setError(
          'Nothing was saved: a show in this review is no longer run on this installation. Your choices are kept. Review them again to see which classes are affected.'
        )
        setPreview(null)
        void query.refetch()
      } else {
        setError(parseApiErrorMessage(e, 'Could not save. Your choices are retained.'))
        if (e instanceof Error && 'status' in e && e.status === 409) {
          setPreview(null)
          setStale(true)
          setDivisionAck(false)
          setDraft(current =>
            current
              ? {
                  ...current,
                  operations: current.operations.map(o => ({
                    ...o,
                    acknowledgeDivisionChange: false,
                  })),
                }
              : null
          )
          void query.refetch()
        }
      }
    }
  }
  const candidateIds = new Map(
    (data?.candidates ?? [])
      .filter(c => c.classId === selected[0])
      .map(c => [c.candidateId, c.evidence])
  )
  const findRows = (data?.classes ?? [])
    .filter(
      c =>
        !selected.includes(c.id) &&
        c.state !== 'unmatched' &&
        (!rows[0] || c.showId !== rows[0].showId) &&
        `${c.effectiveName} ${c.code}`.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const rank = (id: string) =>
        candidateIds.get(id) === 'model' ? 0 : candidateIds.has(id) ? 1 : 2
      return rank(a.id) - rank(b.id)
    })
  function className(id: string) {
    const c = data?.classes.find(c => c.id === id)
    return c
      ? `${data?.editions.find(e => e.showId === c.showId)?.year} · Class ${c.code} · ${c.effectiveName}`
      : id
  }
  function valueName(value: unknown) {
    if (typeof value !== 'string') return 'Not set'
    return (
      data?.models.find(m => m.id === value)?.name ??
      data?.models.flatMap(m => m.subtypes).find(s => s.id === value)?.name ??
      data?.identities.find(i => i.id === value)?.label ??
      draft?.newIdentities.find(i => i.id === value)?.label ??
      value
    )
  }
  if (query.isPending) return <p role="status">Loading championship classes…</p>
  if (query.isError || !data)
    return (
      <div role="alert">
        <p>Could not load classes. {query.error?.message}</p>
        <Button onClick={() => void query.refetch()}>Try again</Button>
      </div>
    )
  const visible = data.classes.filter(
    c =>
      (status === 'all' || c.state === status) &&
      (division === 'all' || c.division.code === division) &&
      `${c.effectiveName} ${c.code}`.toLowerCase().includes(search.toLowerCase())
  )
  return (
    <div className="space-y-6 min-w-0">
      <Link className="underline" to={returnTo}>
        Back to report
      </Link>
      <header>
        <h2 ref={heading} tabIndex={-1} className="text-2xl font-semibold">
          {mode === 'result' ? 'Matching saved' : 'Match classes across years'}
        </h2>
        <p>{series.name}</p>
        <p className="text-sm text-muted-foreground">
          {data.editions.map(e => `${e.year} · ${e.name}`).join(' / ')}
        </p>
        {data.editions.map(
          e =>
            e.ownershipRestriction && (
              <p key={e.showId} className="text-sm">
                {e.year} · {e.name} {NOT_RUN_HERE[e.ownershipRestriction]} Its classes can still be
                compared.
              </p>
            )
        )}
      </header>
      <Dialog
        open={blocker.state === 'blocked'}
        onOpenChange={open => {
          if (!open && blocker.state === 'blocked') blocker.reset()
        }}
      >
        <DialogContent
          role="alertdialog"
          onCloseAutoFocus={e => {
            e.preventDefault()
            heading.current?.focus()
          }}
        >
          <DialogTitle>Unsaved matching changes</DialogTitle>
          <DialogDescription>You have unsaved choices. Leave without saving?</DialogDescription>
          <div className="flex gap-3 mt-4">
            <Button
              disabled={busy}
              onClick={() => blocker.state === 'blocked' && blocker.proceed()}
            >
              Leave without saving
            </Button>
            <Button onClick={() => blocker.state === 'blocked' && blocker.reset()}>
              Keep reviewing
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {error && (
        <p role="alert" className="text-destructive whitespace-pre-line">
          {error}
        </p>
      )}
      {query.isFetching && <p role="status">Updating class information…</p>}
      {mode === 'list' && (
        <>
          <p>
            Match the same class from different years so the report can show how its number of
            entries changed. Each year keeps its own figures.
          </p>
          <fieldset disabled={dirty || busy} className="flex flex-wrap gap-3">
            <legend className="font-medium mb-2">Years to compare (up to 10)</legend>
            {allEditions.map(e => (
              <label key={e.showId} className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={chosenYears.includes(e.year)}
                  disabled={!chosenYears.includes(e.year) && chosenYears.length >= 10}
                  onChange={() => {
                    const next = chosenYears.includes(e.year)
                      ? chosenYears.filter(y => y !== e.year)
                      : [...chosenYears, e.year]
                    if (!next.length) {
                      setError('Select at least one year.')
                      return
                    }
                    lastReadable.current = null
                    const p = new URLSearchParams(params)
                    p.set('years', next.sort().join(','))
                    setParams(p)
                  }}
                />
                {e.year}
              </label>
            ))}
          </fieldset>
          <dl className="grid grid-cols-3 gap-4 border-y py-4">
            {[
              ['Classes to review', data.counts.review],
              ['Classes matched', data.counts.matched],
              ['Excluded from comparison', data.counts.excluded],
            ].map(([name, n]) => (
              <div key={name}>
                <dt className="text-sm">{name}</dt>
                <dd className="text-2xl font-semibold">{n}</dd>
              </div>
            ))}
          </dl>
          {suggestions.length > 0 && (
            <section aria-label="Suggested matches" className="space-y-3 rounded-md border p-4">
              <h3 className="text-lg font-semibold">Suggested matches</h3>
              <p>
                These classes have the same name, ignoring their class numbers. Ready matches are
                selected for you. Uncheck any you want to leave for later.
              </p>
              <p className="text-sm text-muted-foreground">
                This covers all selected years and divisions, regardless of the filters below.
              </p>
              <Button
                disabled={
                  busy || !includedSuggestions.length || includedSuggestions.some(suggestionProblem)
                }
                onClick={confirmSuggestions}
              >
                {busy
                  ? 'Saving…'
                  : `Confirm ${includedSuggestions.length} ${includedSuggestions.length === 1 ? 'match' : 'matches'}${draft ? ' and pending changes' : ''}`}
              </Button>
              {suggestions.some(suggestionProblem) && (
                <p className="text-sm">
                  {suggestions.filter(g => Boolean(suggestionProblem(g))).length} other same-name
                  groups need attention and will be left for later.
                </p>
              )}
              <ul className="divide-y max-h-[36rem] overflow-y-auto">
                {suggestions.map(g => {
                  const type = data.models.find(m => m.id === g.model)
                  const needsType = !g.identityId && !g.classes.some(c => c.catalogClassId)
                  const problem = suggestionProblem(g)
                  return (
                    <li key={g.key} className="py-3 space-y-2">
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={!skippedSuggestions.includes(g.key) && !problem}
                          disabled={busy || Boolean(problem)}
                          onChange={e =>
                            setSkippedSuggestions(current =>
                              e.target.checked
                                ? current.filter(k => k !== g.key)
                                : [...current, g.key]
                            )
                          }
                        />
                        <span>
                          <strong>{g.name}</strong>
                          <span className="block text-sm">
                            {g.classes
                              .map(
                                c =>
                                  `${data.editions.find(e => e.showId === c.showId)?.year}: ${c.code}`
                              )
                              .join(' · ')}{' '}
                            · {g.classes[0].division.name}
                          </span>
                        </span>
                      </label>
                      {needsType &&
                      data.canRepair &&
                      (!g.model ||
                        Boolean(type?.subtypes.length && !g.subtype) ||
                        suggestionTypes[g.key]) ? (
                        <div className="flex flex-wrap gap-3">
                          <label>
                            Exhibit type for {g.name}
                            <select
                              className={selectStyle}
                              value={g.model}
                              disabled={busy}
                              onChange={e =>
                                setSuggestionTypes(current => ({
                                  ...current,
                                  [g.key]: { model: e.target.value, subtype: '' },
                                }))
                              }
                            >
                              <option value="">Choose an exhibit type</option>
                              {data.models
                                .filter(m => !m.retired)
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(m => (
                                  <option key={m.id} value={m.id}>
                                    {m.name}
                                  </option>
                                ))}
                            </select>
                          </label>
                          {!!type?.subtypes.length && (
                            <label>
                              Subtype for {g.name}
                              <select
                                className={selectStyle}
                                value={g.subtype}
                                disabled={busy}
                                onChange={e =>
                                  setSuggestionTypes(current => ({
                                    ...current,
                                    [g.key]: { model: g.model, subtype: e.target.value },
                                  }))
                                }
                              >
                                <option value="">Choose a subtype</option>
                                {type.subtypes
                                  .filter(t => !t.retired)
                                  .map(t => (
                                    <option key={t.id} value={t.id}>
                                      {t.name}
                                    </option>
                                  ))}
                              </select>
                            </label>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm">
                          {type?.name}
                          {g.subtype ? ` · ${valueName(g.subtype)}` : ''}
                        </p>
                      )}
                      {g.classes.some(
                        c =>
                          c.catalogClassId !== g.model || (c.catalogSubtypeId ?? '') !== g.subtype
                      ) &&
                        g.model && (
                          <p className="text-sm">
                            Saving also links these classes to this exhibit type.
                          </p>
                        )}
                      {problem && <p className="text-sm">{problem}</p>}
                      <Button
                        variant="link"
                        disabled={busy}
                        onClick={() => {
                          compare(g.classes[0])
                          setSelected(g.classes.map(c => c.id))
                          setEditingClassIds(g.classes.map(c => c.id))
                          setModel(g.model)
                          setSubtype(g.subtype)
                          setLabel(g.name)
                          if (g.identityId) setIdentity(g.identityId)
                        }}
                      >
                        Inspect {g.name}
                      </Button>
                    </li>
                  )
                })}
              </ul>
              <p className="text-sm">
                One save for the selected matches. Original class names, rules and show results stay
                unchanged.
              </p>
            </section>
          )}
          {!draft && dirty && (
            <Button variant="outline" disabled={busy} onClick={discard}>
              Reset choices
            </Button>
          )}
          {draft && (
            <div className="flex gap-3 items-center flex-wrap">
              <p>{draft.operations.length} classes have unsaved decisions.</p>
              <Button onClick={() => void review(draft)}>Review pending changes</Button>
              <Button variant="outline" onClick={discard}>
                Discard pending changes
              </Button>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <label>
              Find a class
              <Input
                value={search}
                placeholder="Class name or number"
                onChange={e => {
                  setSearch(e.target.value)
                  setLimit(50)
                }}
              />
            </label>
            <label>
              Division
              <select
                className={selectStyle}
                value={division}
                onChange={e => setDivision(e.target.value)}
              >
                <option value="all">All divisions</option>
                {[
                  ...new Map(data.classes.map(c => [c.division.code, c.division.name])).entries(),
                ].map(([code, name]) => (
                  <option key={code} value={code}>
                    {code} · {name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Review status
              <select
                className={selectStyle}
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                <option value="unresolved">Classes to review</option>
                <option value="assigned">Matched classes</option>
                <option value="unmatched">Excluded from comparison</option>
                <option value="all">All classes</option>
              </select>
            </label>
          </div>
          {!visible.length && <p>No classes in this view. Choose another filter or year.</p>}
          <ul className="divide-y">
            {visible.slice(0, limit).map(c => (
              <li key={c.id} className="py-4 flex flex-wrap justify-between gap-3">
                <div>
                  <h3 className="font-medium">{c.effectiveName}</h3>
                  <p className="text-sm">
                    {className(c.id)} · {c.division.name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {c.state === 'unmatched'
                      ? 'Excluded from year-to-year comparison'
                      : c.state === 'assigned'
                        ? 'Matched'
                        : c.catalogClassId
                          ? 'Ready to review'
                          : 'Exhibit type needs confirmation'}
                  </p>
                </div>
                <Button variant="outline" onClick={() => compare(c)}>
                  {c.state === 'unmatched' ? 'Review again' : 'Compare classes'}
                </Button>
              </li>
            ))}
          </ul>
          {visible.length > limit && (
            <Button variant="outline" onClick={() => setLimit(limit + 50)}>
              Show more classes
            </Button>
          )}
        </>
      )}
      {mode === 'compare' && (
        <>
          <h3 className="text-lg font-semibold">Match these classes across years</h3>
          <p>
            Class numbers can change. Check the kind of exhibit and who can enter before matching.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {rows.map(c => (
              <article key={c.id} className="border rounded-md p-4 space-y-3 min-w-0">
                <h4 className="font-semibold">{className(c.id)}</h4>
                <dl className="text-sm space-y-2">
                  <div>
                    <dt className="font-medium">Division</dt>
                    <dd>
                      {c.division.code} · {c.division.name}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium">Exhibit type</dt>
                    <dd>
                      {c.exhibitType ?? 'Needs confirmation'}
                      {c.subtype ? ` / ${c.subtype}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium">Number of entries</dt>
                    <dd>{c.count}</dd>
                  </div>
                  <div>
                    <dt className="font-medium">Original rules and requirements</dt>
                    <dd className="whitespace-pre-wrap break-words">
                      {c.rules ??
                        'Rules are not recorded. Check the original schedule before deciding.'}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium">Class description / eligibility</dt>
                    <dd className="whitespace-pre-wrap break-words">
                      {c.eligibility ??
                        'Eligibility is not recorded separately. Check the original rules.'}
                    </dd>
                  </div>
                </dl>
                {rows.length > 1 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelected(selected.filter(id => id !== c.id))
                    }}
                  >
                    Remove from this comparison
                  </Button>
                )}
              </article>
            ))}
          </div>
          <section className="space-y-3">
            <h3 className="font-medium">Find a corresponding class in another year</h3>
            <label>
              Search other classes
              <Input
                value={search}
                onChange={e => {
                  setSearch(e.target.value)
                  setCandidateLimit(6)
                }}
                placeholder="Class name or number"
              />
            </label>
            <ul className="divide-y max-w-full">
              {findRows.slice(0, candidateLimit).map(c => (
                <li key={c.id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                  <span>
                    {className(c.id)}
                    {candidateIds.has(c.id) && (
                      <small className="block text-muted-foreground">
                        {candidateIds.get(c.id) === 'model'
                          ? 'Same exhibit type and division'
                          : 'Same name and division — check the rules'}
                      </small>
                    )}
                  </span>
                  <Button
                    variant="outline"
                    disabled={rows.some(r => r.showId === c.showId)}
                    onClick={() => {
                      setSelected([...selected, c.id])
                      if (c.identityId) setIdentity(c.identityId)
                    }}
                  >
                    Compare this class
                  </Button>
                </li>
              ))}
            </ul>
            {findRows.length > candidateLimit && (
              <Button variant="outline" onClick={() => setCandidateLimit(n => n + 6)}>
                Show more possible matches
              </Button>
            )}
            {!findRows.length && <p>No other class found. Change the search or decide later.</p>}
          </section>
          <section className="border rounded-md p-4 space-y-4" aria-label="Matching decision">
            {activeRows.length > 0 && (
              <label className="block">
                Match with
                <select
                  className={selectStyle}
                  value={identity}
                  onChange={e => {
                    setIdentity(e.target.value)
                  }}
                >
                  <option value="">Choose an existing match</option>
                  {data.canCreateIdentity && <option value="new">Start a new match</option>}
                  {activeRows.map(i => (
                    <option key={i.id} value={i.id} disabled={!i.catalogClassId}>
                      {i.label} · {i.divisionCode}
                      {!i.catalogClassId && ' — needs an exhibit type'}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {identity === 'new' && data.canCreateIdentity && (
              <>
                <label className="block">
                  Shared class name
                  <Input value={label} onChange={e => setLabel(e.target.value)} />
                </label>
                <label className="block">
                  What kind of exhibit do these classes require?
                  <select
                    className={selectStyle}
                    value={model}
                    onChange={e => {
                      setModel(e.target.value)
                      setSubtype('')
                    }}
                  >
                    <option value="">Choose an exhibit type</option>
                    {[false, true].map(retired => (
                      <optgroup
                        key={String(retired)}
                        label={retired ? 'Historical exhibit types' : 'Current exhibit types'}
                      >
                        {data.models
                          .filter(m => m.retired === retired)
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(m => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                              {m.retired ? ' (historical)' : ''}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                </label>
                {Boolean(targetModel?.subtypes.length) && (
                  <label className="block">
                    Which kind of {targetModel?.name}?
                    <select
                      className={selectStyle}
                      value={subtype}
                      onChange={e => {
                        setSubtype(e.target.value)
                      }}
                    >
                      <option value="">Choose a subtype</option>
                      {targetModel?.subtypes.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                          {s.retired ? ' (historical)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </>
            )}
            {!data.canCreateIdentity && !activeRows.length && (
              <p>An administrator needs to set up the first match for these classes.</p>
            )}
            {deniedRepair && (
              <p role="alert">
                An administrator must confirm the exhibit type for these classes before you can save
                this match.
              </p>
            )}
            {data.canRepair && (
              <p className="text-sm">
                No suitable exhibit type?{' '}
                <a className="underline" href="/catalog/classes" target="_blank" rel="noreferrer">
                  Open catalog review in a new tab
                </a>
                . Your choices stay here.
              </p>
            )}
            {needsRepair && chosenModel && data.canRepair && (
              <p className="text-sm">
                Saving also sets the exhibit type to {valueName(chosenModel)}
                {chosenSubtype ? ` / ${valueName(chosenSubtype)}` : ''} for the selected classes
                whose type differs or is missing. Original rules and results stay unchanged.
              </p>
            )}
            <details>
              <summary className="cursor-pointer underline">Add a note (optional)</summary>
              <label className="block">
                Note (optional)
                <textarea
                  className={selectStyle}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                />
              </label>
            </details>
            {preview?.blockers.some(b => b.code === 'SERIES_DIVISION_REVIEW') && (
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={divisionAck}
                  onChange={e => setDivisionAck(e.target.checked)}
                />
                I reviewed the different divisions and confirm this match.
              </label>
            )}
            <Button disabled={cannotSaveMatch} onClick={() => stage('assigned')}>
              {busy ? 'Saving…' : hasOtherDraft ? 'Save all pending changes' : 'Save match'}
            </Button>
            <Button
              variant="outline"
              disabled={cannotSaveMatch}
              onClick={() => stage('assigned', false)}
            >
              Add to batch
            </Button>
          </section>
          <section className="space-y-3" aria-label="Other choices">
            <h3 className="font-medium">These may not be a match</h3>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setSelected([])
                  setSearch('')
                  changeMode('list')
                }}
              >
                I’m not sure — decide later
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSelected(selected.slice(0, 1))
                  setSearch('')

                  setMessage('Find another class using the search above. No decision was saved.')
                }}
              >
                Look for a different match
              </Button>
            </div>
            <p>{message}</p>
            <p>
              Leaving these classes out of the year-to-year comparison preserves their entries and
              results in each show, including annual totals.
            </p>
            <Button
              variant="outline"
              disabled={busy || !canReview}
              className="h-auto max-w-full whitespace-normal py-2 text-left"
              onClick={() => stage('unmatched')}
            >
              Leave these classes out of the year-to-year comparison
            </Button>
            {rows.some(c => c.state === 'unmatched') && (
              <Button
                variant="outline"
                disabled={busy || !canReview}
                onClick={() => stage('unresolved')}
              >
                Put these classes back on the review list
              </Button>
            )}
          </section>
        </>
      )}
      {mode === 'review' && draft && (
        <section className="space-y-4" aria-label="Review changes">
          <h3 className="text-lg font-semibold">Review before saving</h3>
          <p>
            {draft.operations.length} classes · {draft.newIdentities.length} new matches. Nothing is
            saved yet.
          </p>
          <ul className="divide-y">
            {draft.operations.map(o => (
              <li key={o.classId} className="py-3">
                <strong>{className(o.classId)}</strong>
                <p className="text-sm">{draft.notes[o.classId] ?? draft.reason}</p>
                <p className="text-sm">
                  Before:{' '}
                  {data.classes.find(c => c.id === o.classId)?.state === 'assigned'
                    ? `Matched to ${valueName(data.classes.find(c => c.id === o.classId)?.identityId)}`
                    : data.classes.find(c => c.id === o.classId)?.state === 'unmatched'
                      ? 'Excluded from comparison'
                      : 'Needs review'}
                </p>
                <p>
                  {o.state === 'unmatched'
                    ? 'Exclude from year-to-year comparisons. Entries remain in annual totals.'
                    : o.state === 'unresolved'
                      ? 'Put back on the review list.'
                      : `Match as: ${valueName(o.identityId)}`}
                </p>
              </li>
            ))}
          </ul>
          {preview?.changes
            .filter(c => c.entityType !== 'decision')
            .map(c => (
              <div key={`${c.entityType}:${c.entityId}`} className="border rounded-md p-3">
                <h4 className="font-medium">
                  {c.entityType === 'class'
                    ? className(c.entityId)
                    : `New shared class name: ${c.after.label}`}
                </h4>
                <p>
                  Exhibit type: {valueName(c.before?.catalogClassId)} →{' '}
                  {valueName(c.after.catalogClassId)}
                </p>
                {Boolean(c.before?.catalogSubtypeId || c.after.catalogSubtypeId) && (
                  <p>
                    Subtype: {valueName(c.before?.catalogSubtypeId)} →{' '}
                    {valueName(c.after.catalogSubtypeId)}
                  </p>
                )}
                <p className="text-sm">{c.reason}</p>
              </div>
            ))}
          {previewMutation.isPending && <p role="status">Checking the complete review…</p>}
          {preview?.blockers.map((b, i) => (
            <p key={i} role="alert" className="text-destructive">
              {b.message} {b.classIds.map(className).join('; ')}
            </p>
          ))}
          <p>
            Original rules, entries, results and Exhibit IDs stay unchanged. Public reports are not
            republished.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => {
                const classId =
                  preview?.blockers.flatMap(b => b.classIds)[0] ?? draft.operations[0]?.classId
                const c = data.classes.find(c => c.id === classId)
                if (c) compare(c)
                else changeMode('compare')
              }}
            >
              Edit choices
            </Button>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => {
                setSelected([])
                setSearch('')
                changeMode('list')
              }}
            >
              Review more classes
            </Button>
            {stale && (
              <section aria-label="Updated class details" className="space-y-3">
                <h4 className="font-medium">
                  Check the updated details for every class in this review
                </h4>
                {draft.operations.map(o => {
                  const c = data.classes.find(c => c.id === o.classId)
                  return (
                    <article key={o.classId} className="border rounded-md p-3">
                      <strong>{className(o.classId)}</strong>
                      <p className="whitespace-pre-wrap">{c?.rules ?? 'No rules recorded'}</p>
                      <p>{c?.eligibility ?? 'Check eligibility in the original schedule.'}</p>
                      <p>
                        Current division: {c?.division.code} · {c?.division.name}
                      </p>
                      <p>Current entries: {c?.count ?? 'Unavailable'}</p>
                      <p>Current subtype: {c?.subtype ?? 'Not recorded'}</p>
                      <p>Current exhibit type: {c?.exhibitType ?? 'Not confirmed'}</p>
                    </article>
                  )
                })}
              </section>
            )}
            {!preview && (
              <Button
                disabled={busy || query.isFetching}
                onClick={() => void review(stale ? { ...draft, revision: data.revision } : draft)}
              >
                {stale ? 'Review updated details' : 'Review again'}
              </Button>
            )}
            <Button disabled={busy || !preview?.canApply} onClick={() => void apply()}>
              {save.isPending
                ? 'Saving…'
                : draft.operations.every(o => o.state === 'assigned')
                  ? new Set(draft.operations.map(o => o.identityId)).size === 1
                    ? 'Save this match'
                    : `Save ${new Set(draft.operations.map(o => o.identityId)).size} matches`
                  : 'Save reviewed changes'}
            </Button>
          </div>
        </section>
      )}
      {mode === 'result' && (
        <section className="space-y-4">
          <p role="status">{message}</p>
          <ul className="list-disc pl-5">
            {receipt?.summary.map(line => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p>
            {data.counts.matched} classes matched. {data.counts.review} classes still need review.
          </p>
          <p className="text-sm">Review receipt: {receipt?.id}</p>
          <p>
            This updates the internal report. Finalizing and publishing shows are separate steps.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => {
                setReceipt(null)
                setMessage('')
                changeMode('list')
              }}
            >
              Continue reviewing
            </Button>
            <Link className="underline" to={reportUrl}>
              View the updated report
            </Link>
          </div>
        </section>
      )}
    </div>
  )
}
